import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	inject,
	Input,
	Output,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { v4 as uuidV4 } from "uuid";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => ToggleComponent),
	multi: true,
};

@Component({
	selector: "ic-toggle",
	styleUrls: ["./input.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./input.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ToggleComponent implements ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);

	@Input() label: string;
	@Input() inputClass = "";
	@Input() id = uuidV4();
	@Input() error = false;
	@Input() help = "";
	@Input() required = true;
	/** Define if the input should have padding. */
	@Input() padding: "default" | "none" = "default";

	@Output() checked = new EventEmitter<boolean>();

	/** Display template to replace the default toggle layout. */
	@ContentChild("option", { static: false }) optionTemplate: TemplateRef<any>;
	/** Actual toggle input (needed for the ToggleGroupDirective) */
	@ViewChild("input", { static: true }) input: ElementRef;

	value = false;
	disabled = false;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: boolean) {
		this.value = value;
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	onChange(value: boolean) {
		this.value = value;
		this.onModelChange(value);
		this.checked.emit(value);
		this.onTouch();
	}

	onTouched() {
		this.onTouch();
	}

	markForCheck() {
		this.cdRef.markForCheck();
	}
}
